import { useInView } from '@cian/valuation-utils-component';
import * as React from 'react';

import { useRecommendations } from './useRecommendations';
import { RecommendationSkeleton } from '../../components/RecommendationSkeleton';
import { EPaginationStatus } from '../../types/recommendation';
import { RecommendationsList } from '../../components/RecommendationsList';
import { Recommendation } from '../../components/Recommendation';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { LabelsContainer } from '../LabelsContainer';
import { PaginationError } from '../../components/PaginationError';
import { ELoadingStatus } from '../../types/loadingStatus';
import { PaginationSentinel } from '../../components/PaginationSentinel';
import { RecentlyViewedContainer } from '../RecentlyViewedContainer';
import { RECENTLY_VIEWED_POSITION } from './constants';

export const RecommendationsListContainer: React.FC = () => {
  const {
    recommendations,
    hasMore,
    recommendationsStatus,
    paginationStatus,
    onLoad,
    onLoadMore,
    onRetry,
    onRecommendationClick,
    onShow,
    onTrack,
    onShowTrap,
    isOfferVisited,
  } = useRecommendations();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const recommendationsCountRef = React.useRef(recommendations.length);

  const recommendationsSkeleton = React.useMemo(
    () => new Array(8).fill(0).map((_, index) => <RecommendationSkeleton key={`recommendation-skeleton-${index}`} />),
    [],
  );

  React.useEffect(() => {
    if (inView && hasMore && paginationStatus === EPaginationStatus.Idle) {
      onLoadMore();
    }
  }, [hasMore, inView, onLoadMore, paginationStatus]);

  React.useEffect(() => {
    const previousRecommendationsCount = recommendationsCountRef.current;

    if (recommendations.length > previousRecommendationsCount && previousRecommendationsCount > 0) {
      onShow();
    }

    recommendationsCountRef.current = recommendations.length;
  }, [recommendations.length, onShow]);

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return <RecommendationsList onLoad={onLoad}>{recommendationsSkeleton}</RecommendationsList>;
  }

  if (!recommendations.length) {
    return null;
  }

  return (
    <RecommendationsList onShow={onShow}>
      {recommendations.map((recommendation, index) => (
        <React.Fragment key={`recommendation-${index}`}>
          <Recommendation
            onTrack={onTrack}
            position={index + 1}
            key={`recommendation-${index}`}
            onClick={onRecommendationClick}
            onShowTrap={onShowTrap}
            favoriteButton={<FavoriteButtonContainer id={recommendation.offerId} />}
            labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
            {...recommendation}
          />
          {index === RECENTLY_VIEWED_POSITION && <RecentlyViewedContainer />}
        </React.Fragment>
      ))}
      {paginationStatus === EPaginationStatus.Loading && recommendationsSkeleton}
      {paginationStatus === EPaginationStatus.Error && <PaginationError onClick={onRetry} />}
      {paginationStatus !== EPaginationStatus.Error && hasMore && <PaginationSentinel ref={ref} />}
    </RecommendationsList>
  );
};
